/**
 * LiquidMorphIndicator —— iOS 26 浮动 tab bar 的 spring 液态流动胶囊指示器。
 * ------------------------------------------------------------------
 * Apple 标志性动画：选中项背后的玻璃胶囊，在选项之间用 spring 物理平滑
 * morph 流动（非瞬切）。framer-motion `layoutId` 把分散在各选项中的同名
 * 指示器视为同一元素，active 变化时自动 spring 过渡位置/尺寸（shared layout）。
 *
 *  材质：玻璃胶囊（backdrop-blur + saturate + 凸面 specular rim，token 驱动）。
 *  这是 chrome 级材质（一次仅一个可见），非控件级，遵循 Apple「use sparingly」。
 *
 * 用法（dock/subnav/tabs 选中态）：
 *   {active && <LiquidMorphIndicator layoutId="dock-active" />}
 *   <Icon className="relative z-[1] ..." />
 *
 * 通用性：纯 token（--glass-* / --squircle-*），非 liquid 主题读 fallback 亦获玻璃材质。
 * prefers-reduced-motion 由组件内 useReducedMotion() 自动降级为瞬切（duration:0），调用方无需处理。
 */
import { motion, useReducedMotion, type Transition } from 'framer-motion';
import { cn } from '@/lib/utils';
import { liquidSpring } from '@/lib/motion/springs';

/** morph 指示器默认走 snappy spring（SwiftUI .snappy 视觉等价；集中维护于 springs 库）。 */
const DEFAULT_SPRING: Transition = liquidSpring.snappy;

export interface LiquidMorphIndicatorProps {
  /** 同组唯一；framer-motion 据此在选项间 morph 流动 */
  layoutId: string;
  className?: string;
  /** spring 物理参数（覆盖默认 Apple-like 曲线） */
  transition?: Transition;
}

export function LiquidMorphIndicator({
  layoutId,
  className,
  transition = DEFAULT_SPRING,
}: LiquidMorphIndicatorProps) {
  // 尊重 prefers-reduced-motion：morph 瞬切（layoutId 仍定位选中项，仅跳过 spring 动画）
  const prefersReducedMotion = useReducedMotion();
  return (
    <motion.span
      layoutId={layoutId}
      transition={prefersReducedMotion ? { duration: 0 } : transition}
      aria-hidden="true"
      className={cn('liquid-morph-indicator', className)}
    />
  );
}

export default LiquidMorphIndicator;
